import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { saveRole, type AccessPermission, type AccessRole } from "@/lib/accessControl";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null creates a new role. */
  role: AccessRole | null;
  permissions: AccessPermission[];
}

const SCOPE_MODE_HINT: Record<AccessPermission["scope_mode"], string> = {
  global: "Only when the role applies everywhere",
  scoped: "Within the region, program or course it's assigned to",
  everywhere: "Everywhere, even when the role is limited to one place",
};

function groupByCategory(permissions: AccessPermission[]) {
  const groups = new Map<string, AccessPermission[]>();
  for (const p of permissions) groups.set(p.category, [...(groups.get(p.category) ?? []), p]);
  return [...groups.entries()];
}

export function RoleEditorDialog({ open, onOpenChange, role, permissions }: Props) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setName(role?.name ?? "");
      setDescription(role?.description ?? "");
      setSelected(new Set(role?.permissions ?? []));
    }
  }, [open, role]);

  const groups = useMemo(() => groupByCategory(permissions), [permissions]);

  const save = useMutation({
    mutationFn: () => saveRole({ id: role?.id ?? null, name, description, permissions: [...selected] }),
    onSuccess: () => {
      toast.success(role ? "Role updated" : "Role created");
      qc.invalidateQueries({ queryKey: ["admin", "access"] });
      qc.invalidateQueries({ queryKey: ["my_access_permissions"] });
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggle = (key: string, on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(key); else next.delete(key);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{role ? `Edit ${role.name}` : "New role"}</DialogTitle>
          <DialogDescription>
            Choose what this role can do. Permissions marked “Coming soon” are saved now and start working
            when that part of EAB Connect is switched over.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto pr-1">
          <div className="space-y-2">
            <Label htmlFor="role-name">Name</Label>
            <Input id="role-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="role-description">Description</Label>
            <Textarea
              id="role-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={2}
            />
          </div>

          {groups.map(([category, perms]) => (
            <fieldset key={category} className="space-y-2">
              <legend className="text-sm font-semibold">{category}</legend>
              {perms.map((p) => (
                <label
                  key={p.key}
                  htmlFor={`perm-${p.key}`}
                  className="flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:bg-muted/50"
                >
                  <Checkbox
                    id={`perm-${p.key}`}
                    checked={selected.has(p.key)}
                    onCheckedChange={(v) => toggle(p.key, v === true)}
                    className="mt-0.5"
                  />
                  <div className="space-y-0.5">
                    <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      {p.label}
                      {!p.enforced && <Badge variant="secondary">Coming soon</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">{p.description}</p>
                    <p className="text-xs text-muted-foreground">Applies: {SCOPE_MODE_HINT[p.scope_mode].toLowerCase()}</p>
                  </div>
                </label>
              ))}
            </fieldset>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={name.trim().length < 2 || save.isPending}>
            {save.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {role ? "Save changes" : "Create role"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
